import React from 'react'
import { useRenderTimeoutControl } from '../useRenderTimeoutControl'

export const useExpandToggle = ({
  timeoutMs,
  shouldStartExpanded,
  collapsedHeight = 0,
}: {
  timeoutMs: number
  shouldStartExpanded?: boolean
  collapsedHeight?: number
}): {
  wrapperRef: React.RefObject<HTMLDivElement | null>
  contentRef: React.RefObject<HTMLDivElement | null>
  shouldRender: boolean
  isExpanded: boolean
  toggleExpanded: (shouldForceExpandValue?: boolean) => void
} => {
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const contentRef = React.useRef<HTMLDivElement>(null)
  const timeoutMsRef = React.useRef<number>(timeoutMs)

  const [isExpanded, setIsExpanded] = React.useState(!!shouldStartExpanded)

  const shouldRender = useRenderTimeoutControl({ shouldRender: isExpanded, timeoutMs })

  React.useEffect(() => {
    if (!contentRef.current || !wrapperRef.current) return undefined

    const wrapperEl = wrapperRef.current
    const contentEl = contentRef.current

    if (!isExpanded) {
      wrapperEl.style.height = contentEl.offsetHeight + 'px'
    }

    const animationTimeoutId = setTimeout(() => {
      requestAnimationFrame(() => {
        wrapperEl.style.height = `${isExpanded ? contentEl.offsetHeight : collapsedHeight}px`
      })
    }, 50)

    let autoHeightTimeoutId: ReturnType<typeof setTimeout>
    if (isExpanded) {
      autoHeightTimeoutId = setTimeout(() => {
        wrapperEl.style.height = 'auto'
      }, timeoutMsRef.current + 50)
    }

    return () => {
      if (autoHeightTimeoutId) clearTimeout(autoHeightTimeoutId)
      clearTimeout(animationTimeoutId)
    }
  }, [isExpanded, collapsedHeight])

  const toggleExpanded = React.useCallback((shouldForceExpandValue: boolean | null = null) => {
    if (typeof shouldForceExpandValue === 'boolean') setIsExpanded(shouldForceExpandValue)
    else setIsExpanded(curr => !curr)
  }, [])

  return {
    wrapperRef,
    contentRef,
    shouldRender,
    isExpanded,
    toggleExpanded,
  }
}
